import cx from 'classnames';
import { arrayOf, bool, func, number, shape, string } from 'prop-types';
import { campaignState, getBeautyBoxOpenStatus } from '../../../utils/campaignState';
import env from '../../../utils/env';
import ReactivateCTA from '../SharedComponents/ReactivateCTA/ReactivateCTA';
import ActionCta from './components/ActionCta/ActionCta';
import * as styles from './BoxSubscriptionPanel.module.css';

const BoxSubscriptionPanel = ({ boxSubscription, sysdata, products = [], userExistingCart }) => {
  const handleEditClick = (choiceNumber) => {
    window.location = `${env.envURL}/customize-box/${boxSlug}?q=${choiceNumber}`;
  };

  const handleManageSubscriptionClick = () => {
    window.location = `${env.envURL}/my-account`;
  };

  const renderProductItem = (product, idx) => {
    const hasMultipleVariants = product?.variants?.length > 1;
    const variants = product?.variants ?? [];
    const imageUrl =
      variants[
        hasMultipleVariants
          ? variants.findIndex((variant) => variant.productUuid === product.choiceProductUuid)
          : 0
      ]?.imageUrl;
    return (
      <div key={product?.id} className={styles.productItem}>
        <img
          alt={product?.name}
          className={styles.productImage}
          src={!product?.choiceProductUuid ? null : imageUrl}
        />
        <div className={styles.productDetails}>
          <h4 className={styles.productName}>Choice {idx + 1}</h4>
          <span className={styles.productDescription}>{product?.name}</span>
          {isOpen && (
            <button
              type="button"
              className={styles.editLink}
              onClick={() => {
                handleEditClick(product.choiceNumber);
              }}
            >
              Edit
            </button>
          )}
        </div>
      </div>
    );
  };

  //TODO: Analyze status handling, we decided to omit it for now as per discussed in
  // https://fabfitfun.slack.com/archives/C09AXQRGTDL/p1763579800116639

  const status = getBeautyBoxOpenStatus(boxSubscription);
  const isOpen = status === campaignState.OPEN;
  const displayProducts = products.length > 0 ? products : [];
  const hasProducts = displayProducts.length > 0 && boxSubscription.choices.length > 0;
  const boxSlug =
    boxSubscription?.brandName?.toLowerCase().replace(/\s+/g, '-') +
    '-' +
    boxSubscription?.name?.toLowerCase().replace(/\s+/g, '-');

  const generateBoxSubscriptionChoices = (choices = []) => {
    //TODO: Investigate if this can be dynamic.
    const TOTAL_CHOICES = 4;
    const result = Array(TOTAL_CHOICES).fill(null);

    choices.forEach((choice) => {
      if (choice.choiceNumber >= 1 && choice.choiceNumber <= TOTAL_CHOICES) {
        result[choice.choiceNumber - 1] = choice;
      }
    });

    for (let i = 0; i < TOTAL_CHOICES; i++) {
      if (result[i] === null) {
        result[i] = {
          choiceNumber: i + 1,
          productUuid: null,
          choiceProductUuid: null,
        };
      }
    }

    return result;
  };

  const sortedChoices = generateBoxSubscriptionChoices(boxSubscription.choices);

  const sortedProducts = sortedChoices.map((choice) => {
    const matchedProduct = displayProducts.find((product) => {
      const productMatch = product.productUuid === choice.productUuid;
      const variantMatch = product.variants?.some(
        (variant) => variant.productUuid === choice.productUuid,
      );
      return productMatch || variantMatch;
    });

    // Find the matching variant to get catalogUuid
    const matchingVariant = matchedProduct?.variants?.find(
      (variant) => variant.productUuid === choice.productUuid,
    );

    return {
      ...(matchedProduct ?? {}),
      choiceProductUuid: choice.productUuid,
      choiceNumber: choice.choiceNumber,
      catalogUuid: matchingVariant?.catalogUuid,
    };
  });

  const hasPartialSelections = sortedChoices.some((choice) => choice.choiceProductUuid === null);

  return (
    <>
      <div className={styles.panelContainer}>
        <div className={styles.titleContainer}>
          <h1 className={styles.title}>Beauty Edit Box</h1>
          <div className={styles.manageSubscriptionContainer}>
            <button
              type="button"
              className={styles.manageSubscription}
              onClick={handleManageSubscriptionClick}
            >
              Manage Subscription
            </button>
          </div>
        </div>

        {/* Customizations Section */}
        <div className={styles.customizationsSection}>
          <h2 className={styles.customizationsHeader}>Customizations</h2>
          <p className={styles.description}>
            Thanks for submitting your choices! We’re hard at work processing your box now. You can
            view tracking here as soon as it’s available.
          </p>
          <div className={styles.statusContainer}>
            <div className={styles.status}>
              Status: {status || 'Open'}
              <div
                className={cx(styles.statusLight, {
                  [styles.open]: isOpen,
                })}
              />
            </div>
          </div>
        </div>

        {/* Main Container - matches Panel structure */}
        <div className={cx(styles.container, { [styles.centered]: !hasProducts })}>
          <div className={styles.productGridWrapper}>
            {!hasProducts ? (
              <ReactivateCTA
                baseClass="customize"
                title="Customizations are Open!"
                desc="This is where you’ll see your box customizations after you’ve selected them."
                ctaLabel="Customize Now"
                iconClass="fa-hand-holding-box"
                isMobile={sysdata.isMobile}
                isCustomizationFlow
                boxSlug={boxSlug}
              />
            ) : (
              <div className={styles.productGrid}>{sortedProducts.map(renderProductItem)}</div>
            )}
          </div>
          {hasProducts && (
            <ActionCta
              hasPartialSelections={hasPartialSelections}
              boxSlug={boxSlug}
              isOpen={isOpen}
              userExistingCart={userExistingCart}
              boxSubscription={boxSubscription}
              sortedProducts={sortedProducts}
            />
          )}
        </div>
      </div>
      <div className="hor-rule mar-h-40" />
    </>
  );
};

BoxSubscriptionPanel.propTypes = {
  boxSubscription: shape({}).isRequired,
  sysdata: shape({
    boxData: shape({
      customizeCampaignState: shape({}).isRequired,
      customizations: arrayOf(
        shape({
          additionalItems: arrayOf(
            shape({
              quantity: number.isRequired,
              price: number.isRequired,
            }),
          ).isRequired,
          questionType: string.isRequired,
          additionalItemCost: number.isRequired,
        }),
      ).isRequired,
    }).isRequired,
    userData: shape({}).isRequired,
    isMobile: bool.isRequired,
    isProspect: bool.isRequired,
    revealCallback: func.isRequired,
  }).isRequired,
  products: arrayOf(shape({})),
  userExistingCart: shape({}),
};

BoxSubscriptionPanel.displayName = 'BoxSubscriptionPanel';

export default BoxSubscriptionPanel;
